import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useRef, useState } from "react";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "./PageHeader";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "../auth/access";
import { errorText } from "../utils/apiError";
import { translateKey } from "../i18n/translate";

interface Props {
  premiseId: string;
  rooms: { id: string; name?: string }[];
}

type StatusFilter = "pending" | "approved" | "rejected" | "all";

interface Photo {
  photoId: string;
  roomId: string;
  receivedAt: string;
  expiresAt: string;
  status: string;
  imageUrl: string;
  fromName: string;
  fromEmail: string;
  subject: string;
  caption: string | null;
}

const POLL_MS = 15000;

/**
 * Family-photo moderation queue. Lists inbound photos for the premise, filtered
 * by status (defaults to Pending - the review inbox). Staff approve a photo
 * (it reaches the in-room slideshow immediately via a photos_changed nudge),
 * reject it (hidden from the TV), or hard-delete it. Photos only ever land as
 * "pending" for mailboxes with moderation enabled; otherwise they auto-approve
 * and show up here under Approved.
 */
export default function PhotoModerationQueue({ premiseId, rooms }: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const roomLabel = useCallback(
    (id: string) => {
      const r = rooms.find((x) => x.id === id);
      return r?.name ? `${r.name} (${id})` : id;
    },
    [rooms]
  );

  const refresh = useCallback(async () => {
    try {
      const [list, pend] = await Promise.all([
        foyerApi.listPremiseCarePhotos(premiseId, {
          status: filter === "all" ? undefined : filter,
        }),
        // Keep the Pending badge live regardless of the active filter.
        foyerApi.listPremiseCarePhotos(premiseId, { status: "pending" }),
      ]);
      setPhotos(list as Photo[]);
      setPendingCount(pend.length);
      setError(null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setLoading(false);
    }
  }, [premiseId, filter, t]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh]);

  const act = async (
    photo: Photo,
    action: "approved" | "rejected" | "delete"
  ) => {
    setBusyId(photo.photoId);
    setError(null);
    try {
      if (action === "delete") {
        await foyerApi.deleteRoomCarePhoto(premiseId, photo.photoId);
      } else {
        await foyerApi.setRoomCarePhotoStatus(premiseId, photo.photoId, action);
      }
      toast.success(
        action === "delete"
          ? t("photoModerationQueue.deleted", { room: photo.roomId })
          : action === "approved"
            ? t("photoModerationQueue.approved", { room: photo.roomId })
            : t("photoModerationQueue.rejected", { room: photo.roomId })
      );
      await refresh();
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(
          t,
          action === "delete"
            ? "deleteRoomPhoto"
            : action === "approved"
              ? "approveRoomPhoto"
              : "rejectRoomPhoto",
          err,
          { room: photo.roomId }
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={2}>
      <PageHeader
        eyebrow={t("photoModerationQueue.eyebrow")}
        title={t("photoModerationQueue.title")}
        actions={
          <>
            {pendingCount > 0 && (
              <Chip
                size="small"
                color="warning"
                label={t("photoModerationQueue.toReview", {
                  count: pendingCount,
                })}
                data-testid="photos-pending-count"
              />
            )}
            {loading && <CircularProgress size={16} />}
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Tabs
              value={filter}
              onChange={(_, v) => setFilter(v as StatusFilter)}
              data-testid="photos-filter"
            >
              <Tab
                value="pending"
                label={t("photoModerationQueue.tabs.pending")}
              />
              <Tab
                value="approved"
                label={t("photoModerationQueue.tabs.approved")}
              />
              <Tab
                value="rejected"
                label={t("photoModerationQueue.tabs.rejected")}
              />
              <Tab value="all" label={t("photoModerationQueue.tabs.all")} />
            </Tabs>

            {photos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {filter === "pending"
                  ? t("photoModerationQueue.emptyPending")
                  : t("photoModerationQueue.emptyView")}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 2,
                }}
              >
                {photos.map((photo) => (
                  <Card
                    key={photo.photoId}
                    variant="outlined"
                    data-testid={`photo-card-${photo.photoId}`}
                  >
                    <CardMedia
                      component="img"
                      image={photo.imageUrl}
                      alt={
                        photo.caption ??
                        photo.subject ??
                        t("photoModerationQueue.altFallback")
                      }
                      sx={{
                        height: 180,
                        objectFit: "cover",
                        bgcolor: "action.hover",
                      }}
                    />
                    <CardContent sx={{ pb: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="subtitle2" noWrap>
                          {roomLabel(photo.roomId)}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            photo.status === "approved"
                              ? "success"
                              : photo.status === "rejected"
                                ? "default"
                                : "warning"
                          }
                          label={translateKey(
                            t,
                            `care:photoModerationQueue.status.${photo.status}`,
                            { defaultValue: photo.status }
                          )}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {photo.fromName || photo.fromEmail}
                      </Typography>
                      {photo.caption && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {photo.caption}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions
                      sx={{ px: 2, pb: 1.5, flexWrap: "wrap", gap: 0.5 }}
                    >
                      <PermissionGate
                        resource="residents/photos"
                        level="edit"
                        place={{ premiseId }}
                      >
                        {photo.status !== "approved" && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckIcon />}
                            disabled={busyId === photo.photoId}
                            onClick={() => void act(photo, "approved")}
                            data-testid={`photo-approve-${photo.photoId}`}
                          >
                            {t("common:buttons.approve")}
                          </Button>
                        )}
                        {photo.status !== "rejected" && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<BlockIcon />}
                            disabled={busyId === photo.photoId}
                            onClick={() => void act(photo, "rejected")}
                            data-testid={`photo-reject-${photo.photoId}`}
                          >
                            {t("common:buttons.reject")}
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          disabled={busyId === photo.photoId}
                          onClick={() => void act(photo, "delete")}
                          data-testid={`photo-delete-${photo.photoId}`}
                        >
                          {t("common:buttons.delete")}
                        </Button>
                      </PermissionGate>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
